import { ENGLISH } from 'app/constants/enums/Language';
import { MaterialTuple } from 'app/constants/Materials';
import {
  BotBooking,
  LessonBooking,
  LessonHeader,
  MeetingBooking,
  User,
  Brand,
} from 'app/constants/schemas';
import { CombinedLessonBooking } from 'app/constants/schemas/CombinedLessonBooking';
import { LessonStudent } from 'app/constants/schemas/LessonStudent';
import { LessonTeacher } from 'app/constants/schemas/LessonTeacher';
import { getAuthUser } from 'app/models/auth/selectors';
import { getEntityOfType } from 'app/models/entities/selectors';
import { getMaterialRoute } from 'app/models/route/selectors/materials';
import {
  BookingId,
  BookingQueryParam,
  BookingState,
  BookingType,
  IBookingStateOther,
  ILessonBookingState,
  IMeetingBookingState,
} from 'app/models/rtc/booking/constants';
import { ParticipantType } from 'app/models/rtc/constants';
import Logger from 'app/services/logger';
import { AppState } from 'app/store';
import { EntityStringId } from 'app/types/entity';
import isEqual from 'lodash/isEqual';
import { parse, stringify } from 'query-string';
import LessonBookingRole from 'app/constants/LessonBookingRole';
import { createLessonBookingOutcome, LessonBookingOutcomeType } from 'app/models/rtc/booking/lessons/outcome';

export const getBookingState = (
  state: AppState,
  bookingType: BookingType,
  bookingId: BookingId,
  participantType: ParticipantType,
  currentTime: Date,
  hostKey?: string,
): BookingState | undefined => {
  switch (bookingType) {
    case BookingType.Lesson:
      return getLessonBookingState(state, bookingId, participantType, currentTime);

    case BookingType.Meeting:
      return getMeetingBookingState(state, bookingId, participantType, hostKey);
    case BookingType.TeacherRegistration:
    case BookingType.TestCall:
      return getGenericBookingState(state, bookingType, bookingId, participantType);
  }
};

const getLessonBookingState = (
  state: AppState,
  bookingId: BookingId,
  participantType: ParticipantType,
  currentTime: Date,
): ILessonBookingState | undefined => {
  const combinedLessonBooking: CombinedLessonBooking | undefined =
    getEntityOfType(state, CombinedLessonBooking, bookingId);
  const authUser: User | undefined = getAuthUser(state);

  if (!combinedLessonBooking || !authUser) {
    return undefined;
  }

  const lessonBookings: LessonBooking[] =
    combinedLessonBooking
      .bookings
      .map((id): LessonBooking => {
        const lessonBooking = getEntityOfType(state, LessonBooking, id);

        if (!lessonBooking) {
          Logger.error('Unable to find lesson booking.', { id, combinedLessonBooking });
          throw new Error('Unable to find lesson booking.');
        }

        return lessonBooking;
      })
      .toArray();

  // Important for deciding aspects about the room.
  const firstLessonBooking = lessonBookings[0];

  // A combined lesson should always expose at least one booking.
  if (!firstLessonBooking) {
    Logger.error('No lesson bookings present.', { combinedLessonBooking, lessonBookings });
    throw new Error('No lesson bookings present.');
  }

  const studentId = combinedLessonBooking.student;
  const teacherId = combinedLessonBooking.teacher;
  const student: LessonStudent | undefined = getEntityOfType(state, LessonStudent, studentId);
  const time = {
    end: combinedLessonBooking.time.max.valueOf(),
    start: combinedLessonBooking.time.min.valueOf(),
  };
  const outcome = createLessonBookingOutcome(firstLessonBooking);
  const lessonRole = getLessonRole(state, bookingId);
  const brand: Brand | undefined = student?.brand ? getEntityOfType(state, Brand, student.brand) : undefined;

  const teacher: LessonTeacher | undefined = getEntityOfType(state, LessonTeacher, teacherId);

  const displayName = lessonRole === LessonBookingRole.Teacher ?
    teacher?.name :
    authUser.displayName;

  if (displayName === null || displayName === undefined) {
    Logger.error('Missing required lesson participant information.', { teacher, student, authUser });
    throw new Error('Missing required lesson participant information.');
  }

  // Has the lesson already started?
  const started = currentTime >= combinedLessonBooking.time.min;

  // Was the lesson cancelled and if so did the cancellation occur before the expected start time?
  const cancelledBefore =
    outcome?.type === LessonBookingOutcomeType.Cancelled &&
    outcome.time !== null &&
    outcome.time < combinedLessonBooking.time.min;

  // If the user is a teacher, the room is not joinable before the start of the lesson or in the event the lesson
  // was cancelled before the expected start time. The latter ensures that the behavior for cancellation is consistent
  // with whether the user was on the call or not on the call.
  const joinable = lessonRole === LessonBookingRole.Teacher ? started && !cancelledBefore : true;
  const rtcRoom = joinable ? firstLessonBooking.rtcRoom : null;

  // Generate information for materials and tieUps across bookings.
  const tieUps: ILessonBookingState['tieUps'] = {};
  const materials: ILessonBookingState['materials'] = [];

  lessonBookings.forEach(({ id, category, course, language, lesson }) => {
    // Create the correct type of interace for a Material.
    const material: MaterialTuple | undefined =
      lesson ? { categoryId: category, courseId: course, lessonId: lesson } :
      category ? { categoryId: category } :
      language ? { language } : undefined;

    // Only add the material if it does not match anything in the list.
    if (material && !materials.some((m) => materialMatch(m, material))) {
      materials.push(material);
    }

    // Since the lessons are in order, choose the last booking ID for the lesson tie up.
    if (lesson) {
      const lessonHeader: LessonHeader | undefined = getEntityOfType(state, LessonHeader, lesson);
      const lessonId = (lessonHeader && lessonHeader.masterId) || lesson;
      // This will ensure the lesson booking is accessible for the longest period of time.
      // In theory, master ID should be preferred.
      tieUps[lessonId] = id;
    }
  });

  return {
    bookingId,
    bookingType: BookingType.Lesson,
    lessonRole,
    brand,
    materials,
    navigation: {
      tag: {
        category: firstLessonBooking.category,
        course: firstLessonBooking.course,
        lesson: firstLessonBooking.lesson,
      },
      url: calculateRoute(
        state,
        materials[0] || { language: ENGLISH },
        bookingId,
      ),
    },
    outcome,
    participantType,
    rtcRoom,
    joinParams: {},
    studentId,
    teacherId,
    tieUps,
    time,
    displayName,
  };
};

const getGenericBookingState = (
  state: AppState,
  bookingType: BookingType.TeacherRegistration | BookingType.TestCall,
  bookingId: BookingId,
  participantType: ParticipantType,
): IBookingStateOther | undefined => {
  let booking: BotBooking | undefined;

  switch (bookingType) {
    case BookingType.TeacherRegistration:
    case BookingType.TestCall:
      booking = getEntityOfType(state, BotBooking, bookingId);
      break;
  }

  if (!booking) {
    return undefined;
  }

  return {
    bookingId,
    bookingType,
    participantType,
    rtcRoom: booking.rtcRoom,
    joinParams: {},
  };
};

const getMeetingBookingState = (
  state: AppState,
  bookingId: BookingId,
  participantType: ParticipantType,
  hostKey?: string,
): IMeetingBookingState | undefined => {
  const booking: MeetingBooking | undefined = getEntityOfType(state, MeetingBooking, bookingId);
  const authUser: User | undefined = getAuthUser(state);
  const displayName = authUser?.displayName || undefined;

  if (!booking) {
    return undefined;
  }

  return {
    bookingId,
    bookingType: BookingType.Meeting,
    participantType,
    rtcRoom: booking.rtcRoom,
    joinParams: { host_key: hostKey },
    displayName,
  };
};

/**
 * Helper for calculating the root route for a lesson bookings content.
 */
const calculateRoute = (state: AppState, material: MaterialTuple, bookingId: BookingId) => {
  const route = getMaterialRoute(state, material);

  // Add booking parameters.
  const searchParams = parse(route.search || '');
  return {
    ...route,
    search: `?${stringify({
      ...searchParams,
      [BookingQueryParam.BookingId]: bookingId,
      [BookingQueryParam.BookingType]: BookingType.Lesson,
    })}`,
  };
};

/**
 * Helper to determine if two materials match.
 */
const materialMatch = (m1: MaterialTuple, m2: MaterialTuple) => {
  if ('lessonId' in m1 && 'lessonId' in m2) {
    return m1.lessonId === m2.lessonId;
  }

  return isEqual(m1, m2);
};

export const getLessonRole = (state: AppState, combinedLessonBookingId: EntityStringId): LessonBookingRole => {
  const combinedLessonBooking: CombinedLessonBooking | undefined = getEntityOfType(
    state,
    CombinedLessonBooking,
    combinedLessonBookingId,
  );
  const authUser: User | undefined = getAuthUser(state);

  if (!combinedLessonBooking || !authUser) return LessonBookingRole.Other;
  return authUser.id === combinedLessonBooking.student ? LessonBookingRole.Student :
    authUser.teacher === combinedLessonBooking.teacher ? LessonBookingRole.Teacher :
    LessonBookingRole.Other;
};
